import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { debounce, CancelPromiseHandler, makeCancellablePromise } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { IWorkspaceUser } from 'APP/Tasks/workspace/getWorkspaceUsers';
import { WorkspaceRole } from 'APP/model/workspace/workspaceModel.types';

export interface IWorkspaceMembersListPresenter {
  members: IWorkspaceUser[];
  isLoading: boolean;
  cursor?: string;
  searchText?: string;
  cancelLoadMore: CancelPromiseHandler | null;
  search: () => void;
  init(): void;
  loadMore(): Promise<void>;
  setSearchText(searchText: string): void;
  get hasMore(): boolean;
  onMemberRemove(userId: string): void;
  onUpdateMemberRole(userId: string, role: WorkspaceRole): void;
  onReloadMembersList(): void;
}

const SEARCH_DELAY = 200;

export function useWorkspaceMembersListPresenter(
  searchText?: string
): IWorkspaceMembersListPresenter {
  const presenter = useLocalObservable<IWorkspaceMembersListPresenter>(() => ({
    cursor: undefined,
    isLoading: false,
    members: [],
    cancelLoadMore: null,
    searchText,

    async init(): Promise<void> {
      this.members = [];
      this.cursor = undefined;
      await presenter.loadMore();
    },

    setSearchText(searchText: string): void {
      presenter.searchText = searchText;
    },

    search: debounce(async () => {
      await presenter.init();
    }, SEARCH_DELAY),

    async loadMore(): Promise<void> {
      if (presenter.cancelLoadMore) {
        presenter.cancelLoadMore();
      }

      presenter.isLoading = true;

      const { promise: getWorkspaceMembers, cancel } = makeCancellablePromise(
        Tasks.workspace.getWorkspaceUsers({
          searchText: presenter.searchText,
          cursor: presenter.cursor,
        })
      );

      presenter.cancelLoadMore = cancel;

      const workspaceMembersMembersData = await getWorkspaceMembers;

      if (workspaceMembersMembersData) {
        const { users, cursor } = workspaceMembersMembersData;

        presenter.members.push(...users);
        presenter.cursor = cursor;
      }

      presenter.cancelLoadMore = null;
      presenter.isLoading = false;
    },

    get hasMore(): boolean {
      return !!presenter.cursor;
    },

    onMemberRemove(userId: string): void {
      presenter.members = presenter.members.filter((member) => member.user.id !== userId);
    },

    onUpdateMemberRole(userId: string, role: WorkspaceRole): void {
      const index = presenter.members.findIndex((member) => member.user.id === userId);

      if (index !== -1) {
        presenter.members[index] = {
          ...presenter.members[index],
          role,
        };
      }
    },

    onReloadMembersList(): void {
      presenter.init();
    },
  }));

  useEffect(() => {
    presenter.setSearchText(searchText || '');
    searchText ? presenter.search() : presenter.init();
  }, [searchText]);

  return presenter;
}
